// BlockEditor.jsx - Updated for duration-based events
import React from 'react';

export default function BlockEditor({
  blocks,
  updateBlock,
  removeBlock,
  addInject,
  updateInject,
  removeInject,
  addChoice,
  updateChoice,
  removeChoice
}) {
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {blocks.map((block, blockIndex) => (
        <div 
          key={block.id} 
          className={`rounded-lg shadow-sm border mb-4 relative ${
            block.type === 'choice' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
          }`}
        >
          <div className="p-4">
            {/* Block Header */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    block.type === 'choice' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {block.type === 'choice' ? 'Choice Block' : 'Event Block'}
                  </span>
                  {block.type === 'event' && block.autoProceed && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                      Auto-proceed
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder={`${block.type === 'choice' ? 'Decision' : 'Event'} Title`}
                  value={block.title || ""}
                  onChange={(e) => updateBlock(blockIndex, "title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {block.type === 'event' && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="30"
                    max="1200"
                    placeholder="60"
                    value={block.duration || ""}
                    onChange={(e) => updateBlock(blockIndex, "duration", e.target.value)}
                    className="w-20 px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-500">
                    {block.duration ? formatDuration(block.duration) : '60s'}
                  </span>
                </div>
              )}
              
              <button
                onClick={() => removeBlock(blockIndex)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>

            {/* Description */}
            <textarea
              placeholder={`${block.type === 'choice' ? 'Decision' : 'Event'} Description`}
              value={block.description || ""}
              onChange={(e) => updateBlock(blockIndex, "description", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />

            {/* Event Block Specific */}
            {block.type === 'event' && (
              <>
                {/* Auto-proceed settings */}
                <div className="mb-4 p-3 bg-blue-50 rounded">
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={block.autoProceed !== undefined ? block.autoProceed : true}
                        onChange={(e) => updateBlock(blockIndex, "autoProceed", e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-800 font-medium">Auto-proceed when complete</span>
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Event will run for {block.duration ? formatDuration(block.duration) : '60s'} 
                    {block.autoProceed !== false ? ' then automatically continue to next block(s)' : ' then stop'}
                  </p>
                </div>

                {/* Injects Section */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="font-medium text-sm">Injects</h4>
                    </div>
                    <button
                      onClick={() => addInject(blockIndex)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Add Inject
                    </button>
                  </div>

                  {block.injects?.map((inject, injectIndex) => (
                    <div key={injectIndex} className="flex gap-4 items-center mb-2 p-3 bg-yellow-50 rounded">
                      <input
                        type="number"
                        min="0"
                        max={block.duration || 1200}
                        placeholder="0"
                        value={inject.time || ""}
                        onChange={(e) => updateInject(blockIndex, injectIndex, "time", e.target.value)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-500">s</span>
                      <select
                        value={inject.type || "text"}
                        onChange={(e) => updateInject(blockIndex, injectIndex, "type", e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="news">News</option>
                        <option value="social">Social Media</option>
                        <option value="phone">Phone Call</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Content"
                        value={inject.content || ""}
                        onChange={(e) => updateInject(blockIndex, injectIndex, "content", e.target.value)}
                        className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => removeInject(blockIndex, injectIndex)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Next Connections for Event Blocks */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <h4 className="font-medium text-sm">Next Block(s)</h4>
                  </div>
                  
                  <div className="space-y-2">
                    {block.next?.map((nextId, idx) => (
                      <div key={idx} className="flex gap-2">
                        <select
                          value={nextId}
                          onChange={(e) => {
                            const newNext = [...(block.next || [])];
                            if (e.target.value) {
                              newNext[idx] = e.target.value;
                            } else {
                              newNext.splice(idx, 1);
                            }
                            updateBlock(blockIndex, "next", newNext);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Remove connection</option>
                          {blocks.filter(b => b.id !== block.id).map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.title || b.id} ({b.type})
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newNext = [...(block.next || []), ""];
                        updateBlock(blockIndex, "next", newNext);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Add next block connection
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Choice Block Specific */}
            {block.type === 'choice' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="font-medium text-sm">Choices</h4>
                  </div>
                  <button
                    onClick={() => addChoice(blockIndex)}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    Add Choice
                  </button>
                </div>

                {block.choices?.map((choice, choiceIndex) => (
                  <div key={choiceIndex} className="flex gap-4 items-center mb-2 p-3 bg-green-100 rounded">
                    <input
                      type="text"
                      placeholder="Choice Label"
                      value={choice.label || ""}
                      onChange={(e) => updateChoice(blockIndex, choiceIndex, "label", e.target.value)}
                      className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <select
                      value={choice.targetId || ""}
                      onChange={(e) => updateChoice(blockIndex, choiceIndex, "targetId", e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select target block</option>
                      {blocks.filter(b => b.id !== block.id).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.title || b.id} ({b.type})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeChoice(blockIndex, choiceIndex)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}